import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { getFirebaseStorage } from './firebase';

const STORAGE_PREFIX = 'invoice-logos';
const MAX_BYTES = 5 * 1024 * 1024;
const MAX_DIMENSION = 800;
const JPEG_QUALITY = 0.9;

function sanitizeFilename(name: string): string {
  return name.replace(/[^a-zA-Z0-9.-]/g, '_').slice(0, 80);
}

function resizeToBlob(file: File, maxDimension: number, quality: number): Promise<Blob> {
  if (!file.type.startsWith('image/')) return Promise.resolve(file as unknown as Blob);

  return new Promise((resolve) => {
    const img = new Image();
    const objectUrl = URL.createObjectURL(file);
    img.onload = () => {
      URL.revokeObjectURL(objectUrl);
      let { width: w, height: h } = img;
      if (w > maxDimension || h > maxDimension) {
        if (w > h) {
          h = Math.round((h * maxDimension) / w);
          w = maxDimension;
        } else {
          w = Math.round((w * maxDimension) / h);
          h = maxDimension;
        }
      }
      const canvas = document.createElement('canvas');
      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (!ctx) {
        resolve(file as unknown as Blob);
        return;
      }
      ctx.drawImage(img, 0, 0, w, h);
      canvas.toBlob(
        (blob) => (blob ? resolve(blob) : resolve(file as unknown as Blob)),
        'image/jpeg',
        quality
      );
    };
    img.onerror = () => {
      URL.revokeObjectURL(objectUrl);
      resolve(file as unknown as Blob);
    };
    img.src = objectUrl;
  });
}

function blobToDataUrl(blob: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error ?? new Error('Failed to read logo'));
    reader.readAsDataURL(blob);
  });
}

export interface InvoiceLogoUploadResult {
  url: string;
  /** Base64 data URL for PDF/preview without cross-origin Storage fetch. */
  dataUrl: string;
}

/** Upload a logo for client tax invoices. Returns download URL + embedded data URL for PDF export. */
export async function uploadInvoiceLogo(file: File): Promise<InvoiceLogoUploadResult> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please choose an image file.');
  }
  if (file.size > MAX_BYTES) {
    throw new Error('Image must be under 5MB.');
  }

  const storage = getFirebaseStorage();
  const baseName = sanitizeFilename(file.name.replace(/\.[^.]+$/, '') || 'logo');
  const ts = Date.now();
  const blob = await resizeToBlob(file, MAX_DIMENSION, JPEG_QUALITY);
  const dataUrl = await blobToDataUrl(blob);
  const storageRef = ref(storage, `${STORAGE_PREFIX}/${ts}-${baseName}.jpg`);
  await uploadBytes(storageRef, blob, {
    contentType: 'image/jpeg',
    cacheControl: 'public, max-age=31536000',
  });
  return { url: await getDownloadURL(storageRef), dataUrl };
}
